// @flow

import React from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { localize } from "redux-i18n";

import { formatAuthoredBy } from "@transformers/names";
import actionCreators from "@modules/service/actionCreator";
import employeeActionCreators from "@modules/employee/actionCreator";
import * as serviceSelectors from "@modules/service/selectors";
import { View, Button, GSGrid } from "@components";
import AlertAddBespokeRecipientModal from "./alert-bespoke-recipients-add-modal";
import type { State } from "@store/reducers";
import type { Alert, AlertBespokeRecipient } from "@modules/service/reducer/alerts/types";
import type { HierarchyAssignment } from "@modules/service/types";

type OwnProps = $Exact<{
  clientId: number,
  data: Alert,
  updateBespokeRecipients: (bespokeRecipient: Array<AlertBespokeRecipient>) => void,
}>;

type PropsFromState = $Exact<{
  showAddModal: boolean,
  bespokeRecipient: Object | null,
}>;

type PropsFromDispatch = $Exact<{
  setShowAddModal: typeof actionCreators.SetShowAddAlertRecipientModal,
  setEmployeesDefaultData: typeof employeeActionCreators.SetEmployeesDefaultData,
  selectBespokeRecipient: typeof employeeActionCreators.SelectBespokeRecipient,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;

export class AlertBespokeRecipientsForm extends React.Component<Props> {
  get columns() {
    return [
      {
        name: this.props.t("ALERT_DETAILS_BESPOKE_RECIPIENTS_EMPLOYEE_REF"),
        key: "employeeRefPrimary",
      },
      {
        name: this.props.t("ALERT_DETAILS_BESPOKE_RECIPIENTS_EMPLOYEE_NAME"),
        key: "employeeName",
        align: "left",
        renderCell: ({ row }: { row: AlertBespokeRecipient }) => (
          <div>{formatAuthoredBy({ firstName: row.firstName, lastName: row.lastName })}</div>
        ),
      },
      {
        name: this.props.t("ALERT_DETAILS_BESPOKE_WORK_EMAIL_ADDRESS"),
        key: "workEmail",
        align: "left",
        renderCell: ({ row }: { row: AlertBespokeRecipient }) => {
          return row.workEmail || "";
        },
      },
      {
        name: this.props.t("Hierarchy"),
        key: "assignmentRules",
        align: "left",
        renderCell: ({ row }: { row: AlertBespokeRecipient }) => {
          const val = row.assignmentRules
            ? row.assignmentRules.map((row: HierarchyAssignment) => row.target.label).join(", ")
            : "";
          return (
            <div className={"ellipsis cursor-pointer"} style={{ width: "100px" }} title={val}>
              {val}
            </div>
          );
        },
      },
      {
        name: this.props.t("EDIT"),
        key: "edit",
        renderCell: ({ row }: { row: AlertBespokeRecipient }) => {
          return (
            <span className="material-icons pointer mr-1" onClick={this.handleEditRecord(row)}>
              edit
            </span>
          );
        },
      },
      {
        name: this.props.t("DELETE"),
        key: "delete",
        renderCell: ({ row }: { row: AlertBespokeRecipient }) => {
          return (
            <span
              className="material-icons font-size-26 pointer"
              onClick={this.handleShowDelete(row)}
            >
              delete
            </span>
          );
        },
      },
    ];
  }

  get data() {
    return (this.props.data || {}).bespokeRecipients;
  }

  handleShowAdd = () => {
    this.props.setShowAddModal(true);
  };

  handleShowDelete = (item: AlertBespokeRecipient) => () => {
    this.props.updateBespokeRecipients(this.data.filter(d => d.id !== item.id));
  };

  handleCloseAddModal = () => {
    this.props.setShowAddModal(false);
    this.props.selectBespokeRecipient(null);
    this.props.setEmployeesDefaultData();
  };

  handleSubmitAddModal = (bespokeRecipient: AlertBespokeRecipient) => {
    const records =
      (this.data &&
        this.data.filter((recipient: AlertBespokeRecipient) => {
          return recipient.id !== bespokeRecipient.id;
        })) ||
      [];
    const recipients = [...records, bespokeRecipient];
    this.props.updateBespokeRecipients(recipients);
    this.handleCloseAddModal();
  };

  handleEditRecord = (item: AlertBespokeRecipient) => () => {
    this.props.selectBespokeRecipient(item);
    this.props.setShowAddModal(true);
  };

  render() {
    return (
      <View gap={3} className="gs-container">
        <h6>{this.props.t("ALERT_DETAILS_BESPOKE_RECIPIENTS")}</h6>
        <View direction="row" justifyContent="end">
          <Button
            materialIcon="add_box"
            className="link record-table__add-button mb-3"
            onClick={this.handleShowAdd}
            label={this.props.t("ADD")}
          />
        </View>

        {this.data && (
          <GSGrid
            columns={this.columns}
            rows={this.data}
            noResultsText={this.props.t("NO_BESPOKE_RECIPIENTS_MESSAGE")}
          />
        )}
        {this.props.showAddModal && (
          <AlertAddBespokeRecipientModal
            clientId={this.props.clientId}
            onCancel={this.handleCloseAddModal}
            onSubmit={this.handleSubmitAddModal}
          />
        )}
      </View>
    );
  }
}

const mapStateToProps = (state: State): PropsFromState => ({
  showAddModal: serviceSelectors.getShowAlertAddRecipientModal(state),
  bespokeRecipient: serviceSelectors.getBespokeRecipient(state),
});

const mapDispatchToProps: PropsFromDispatch = {
  setEmployeesDefaultData: employeeActionCreators.SetEmployeesDefaultData,
  setShowAddModal: actionCreators.SetShowAddAlertRecipientModal,
  selectBespokeRecipient: employeeActionCreators.SelectBespokeRecipient,
};

export default compose(
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
)(AlertBespokeRecipientsForm);
